import * as Scrivito from "scrivito";
import { truncate } from "lodash-es";
import { textExtractFromObj } from "./textExtract";
import urlFromBinary from "./urlFromBinary";
import isVideoObj from "./isVideoObj";
import getHomepage from "./getHomepage";

function getMetadata(page) {
  const meta = [
    { name: "twitter:card", content: "summary_large_image" },
    { name: "prerender-status-code", content: "200" },
    { property: "og:type", content: "article" },
    { property: "og:url", content: Scrivito.urlFor(page) },
  ];
  const textExtract = textExtractFromObj(page);

  const robotsIndex = page.get("robotsIndex");
  if (robotsIndex === "no") {
    meta.push({ name: "robots", content: "noindex" });
  }

  const description = page.get("metaDataDescription") || "Hochschule Heilbronn";
  if (description) {
    meta.push({ name: "description", content: description });
  }
  const keywords = page.get("metaDataKeywords") || [];
  if (keywords.length > 0) {
    meta.push({ name: "keywords", content: keywords.join(",") });
  }

  const currentPage = Scrivito.currentPage();
  const root = getHomepage(currentPage);
  if (root) {
    const facebookAppId = root.get("facebookAppId");
    if (facebookAppId) {
      meta.push({ property: "fb:app_id", content: facebookAppId });
    }

    const twitterSite = root.get("twitterSite");
    if (twitterSite) {
      meta.push({ name: "twitter:site", content: twitterSite });
    }
  }

  const tcCreator = page.get("tcCreator");
  if (tcCreator) {
    meta.push({ name: "twitter:creator", content: tcCreator });
  }

  const ogLocale =
    page.get("ogLocale") || localeFromLanguage(page.language()) || "de_DE";
  meta.push({ property: "og:locale", content: ogLocale });

  const ogSiteName =
    page.get("ogSiteName") ||
    (root && root.get("title")) ||
    "Hochschule Heilbronn";
  meta.push({ property: "og:site_name", content: ogSiteName });

  const ogTitle =
    page.get("ogTitle") || page.get("tcTitle") || page.get("title");
  if (ogTitle) {
    meta.push({ property: "og:title", content: ogTitle });
  }

  const ogDescription =
    page.get("ogDescription") ||
    page.get("tcDescription") ||
    truncate(textExtract, { length: 297, separator: /,? +/ });
  if (ogDescription) {
    meta.push({ property: "og:description", content: ogDescription });
  }

  const imageAttributes = [
    "ogImage",
    "tcImage",
    "titleImage",
    "navigationBackgroundImage",
    "image",
  ];
  const ogImage = firstImageUrlForAttributes(page, imageAttributes);
  if (ogImage) {
    meta.push({ property: "og:image", content: ogImage });

    const ogImageAlt = page.get("ogImageAlt");
    if (ogImageAlt) {
      meta.push({ property: "og:image:alt", content: ogImageAlt });
    }

    const ogImageObj = imageAttributes.reduce(
      (found, attr) => found || page.get(attr),
      null
    );
    if (ogImageObj) {
      const width = ogImageObj.metadata().get("width");
      const height = ogImageObj.metadata().get("height");
      if (width) {
        meta.push({ property: "og:image:width", content: String(width) });
      }
      if (height) {
        meta.push({ property: "og:image:height", content: String(height) });
      }
    }
  }

  const ogSection = page.get("ogSection");
  if (ogSection) {
    meta.push({ property: "article:section", content: ogSection });
  }

  const publishedAt = page.firstPublishedAt();
  if (publishedAt) {
    meta.push({
      property: "article:published_time",
      content: publishedAt.toISOString(),
    });
  }

  const modifiedAt = page.lastChanged();
  if (modifiedAt) {
    meta.push({
      property: "article:modified_time",
      content: modifiedAt.toISOString(),
    });
  }

  // Twitter cards mirror OG data; tc* fields kept as legacy fallback
  if (ogTitle) {
    meta.push({ name: "twitter:title", content: ogTitle });
  }

  const twitterDescription =
    page.get("ogDescription") ||
    page.get("tcDescription") ||
    truncate(textExtract, { length: 137, separator: /,? +/ });
  if (twitterDescription) {
    meta.push({ name: "twitter:description", content: twitterDescription });
  }

  if (ogImage) {
    meta.push({ name: "twitter:image", content: ogImage });

    const ogImageAlt = page.get("ogImageAlt");
    if (ogImageAlt) {
      meta.push({ name: "twitter:image:alt", content: ogImageAlt });
    }
  }

  if (process.env.FACEBOOK_VERIFICATION) {
    meta.push({
      name: "facebook-domain-verification",
      content: process.env.FACEBOOK_VERIFICATION,
    });
  }

  return meta;
}

function localeFromLanguage(language) {
  const localeMap = {
    de: "de_DE",
    en: "en_GB",
  };
  return localeMap[language] || null;
}

function firstImageUrlForAttributes(obj, attributes) {
  let url;

  attributes.forEach((attribute) => {
    if (url || isVideoObj(obj)) {
      return;
    }
    const binary = obj.get(attribute);
    url = urlFromBinary(binary);
  });

  return url;
}

export default getMetadata;
